import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Check, RotateCcw, X, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toCanvas, warpPreview } from "@/lib/scanner/cv";

const LABELS = ["top-left", "top-right", "bottom-right", "bottom-left"];
const LOUPE = 116;
const ZOOM = 2.5;

const useBoxSize = (ref) => {
  const [s, setS] = useState({ w: 0, h: 0 });
  useLayoutEffect(() => {
    const el = ref.current;
    const read = () => setS({ w: el.clientWidth, h: el.clientHeight });
    read();
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return s;
};

// Frozen photo with 4 big draggable corners, a magnifier and a live flattened preview.
// Only the area inside the corners is ever kept. onDone(quad in raw-canvas pixels).
export const CornerEditor = ({ cv, page, mustAdjust, onDone, onCancel, onRetake }) => {
  const { raw } = page;
  const W = raw.width;
  const H = raw.height;
  const small = useMemo(() => toCanvas(raw, W, H, 1000), [raw, W, H]);
  const photo = useMemo(() => small.toDataURL("image/jpeg", 0.85), [small]);
  const start = useMemo(() => page.quad.map((p) => ({ x: p.x / W, y: p.y / H })), [page.quad, W, H]);
  const [pts, setPts] = useState(start);
  const [moved, setMoved] = useState(false);
  const [active, setActive] = useState(null);
  const area = useRef(null);
  const loupeRef = useRef(null);
  const previewRef = useRef(null);
  const frame = useRef(0);
  const { w: aw, h: ah } = useBoxSize(area);
  const fit = Math.min(aw / W, ah / H) || 0;
  const bw = W * fit;
  const bh = H * fit;
  const ox = (aw - bw) / 2;
  const oy = (ah - bh) / 2;

  // Live preview of the crop (throttled to one warp per animation frame).
  useEffect(() => {
    cancelAnimationFrame(frame.current);
    frame.current = requestAnimationFrame(() => {
      const out = warpPreview(cv, small, pts.map((p) => ({ x: p.x * small.width, y: p.y * small.height })));
      const c = previewRef.current;
      if (!c) return;
      const k = Math.min(140 / out.width, 96 / out.height);
      c.width = Math.round(out.width * k);
      c.height = Math.round(out.height * k);
      c.getContext("2d").drawImage(out, 0, 0, c.width, c.height);
    });
    return () => cancelAnimationFrame(frame.current);
  }, [cv, small, pts]);

  // Magnifier around the corner being dragged.
  useEffect(() => {
    const c = loupeRef.current;
    if (active === null || !c) return;
    const p = pts[active];
    const ctx = c.getContext("2d");
    const span = (LOUPE / ZOOM) * (small.width / bw);
    ctx.fillStyle = "#000";
    ctx.fillRect(0, 0, LOUPE, LOUPE);
    ctx.drawImage(small, p.x * small.width - span / 2, p.y * small.height - span / 2, span, span, 0, 0, LOUPE, LOUPE);
    ctx.strokeStyle = "#34d399";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(LOUPE / 2 - 12, LOUPE / 2); ctx.lineTo(LOUPE / 2 + 12, LOUPE / 2);
    ctx.moveTo(LOUPE / 2, LOUPE / 2 - 12); ctx.lineTo(LOUPE / 2, LOUPE / 2 + 12);
    ctx.stroke();
  }, [active, pts, small, bw]);

  const pointAt = (e) => {
    const r = area.current.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (e.clientX - r.left - ox) / bw)),
      y: Math.min(1, Math.max(0, (e.clientY - r.top - oy) / bh)),
    };
  };
  const down = (i) => (e) => { e.preventDefault(); e.currentTarget.setPointerCapture?.(e.pointerId); setActive(i); };
  const move = (e) => {
    if (active === null) return;
    const p = pointAt(e);
    setPts((ps) => ps.map((q, i) => (i === active ? p : q)));
    setMoved(true);
  };
  const up = () => setActive(null);
  const poly = pts.map((p) => `${ox + p.x * bw},${oy + p.y * bh}`).join(" ");
  const loupeLeft = active !== null && pts[active].x < 0.5;
  const blocked = mustAdjust && !moved;

  return (
    <div className="fixed inset-0 z-[60] bg-black text-white flex flex-col select-none" data-testid="scanner-corner-editor">
      <div className="flex items-center justify-between px-4 py-3">
        <button onClick={onCancel} className="p-2 -ml-2 rounded-full hover:bg-white/10" aria-label="Cancel" data-testid="corner-cancel-btn"><X className="w-6 h-6" /></button>
        <div className={`text-sm font-semibold text-center px-2 ${mustAdjust ? "text-amber-300" : ""}`} data-testid="corner-instructions">
          {mustAdjust ? "Edges not found \u2014 drag each corner onto the paper" : "Check the corners fit the page"}
        </div>
        <div className="w-10" />
      </div>

      <div ref={area} className="relative flex-1 min-h-0 mx-3 touch-none" onPointerMove={move} onPointerUp={up} onPointerCancel={up}>
        {fit > 0 && (
          <>
            <img src={photo} alt="Captured page" draggable={false} className="absolute pointer-events-none" style={{ left: ox, top: oy, width: bw, height: bh }} />
            <svg className="absolute inset-0 w-full h-full pointer-events-none">
              <defs><mask id="crop-mask"><rect width="100%" height="100%" fill="white" /><polygon points={poly} fill="black" /></mask></defs>
              <rect width="100%" height="100%" fill="rgba(0,0,0,0.55)" mask="url(#crop-mask)" />
              <polygon points={poly} fill="none" stroke="#34d399" strokeWidth="2.5" />
            </svg>
            {pts.map((p, i) => (
              <button key={i} type="button" aria-label={`Move ${LABELS[i]} corner`} onPointerDown={down(i)}
                className="absolute w-16 h-16 -ml-8 -mt-8 grid place-items-center touch-none"
                style={{ left: ox + p.x * bw, top: oy + p.y * bh }} data-testid={`corner-handle-${i}`}>
                <span className={`w-8 h-8 rounded-full border-[3px] border-emerald-400 bg-emerald-400/30 shadow-[0_0_0_2px_rgba(0,0,0,0.5)] transition-transform ${active === i ? "scale-125" : ""}`} />
              </button>
            ))}
          </>
        )}
        {active !== null && (
          <canvas ref={loupeRef} width={LOUPE} height={LOUPE} className={`absolute top-2 ${loupeLeft ? "right-2" : "left-2"} rounded-full border-2 border-emerald-400 shadow-lg pointer-events-none`} data-testid="corner-loupe" />
        )}
      </div>

      <div className="flex items-center justify-center gap-2 pt-2" data-testid="corner-preview">
        <span className="text-[10px] uppercase tracking-wider text-white/60">Preview</span>
        <canvas ref={previewRef} className="block bg-white rounded shadow" />
      </div>

      <div className="px-4 pb-7 pt-3 grid grid-cols-3 gap-2">
        <Button variant="outline" className="h-12 rounded-xl bg-transparent text-white border-white/30 hover:bg-white/10 hover:text-white" onClick={onRetake} data-testid="corner-retake-btn">
          <RotateCcw className="w-4 h-4 mr-1.5" /> Retake
        </Button>
        <Button variant="outline" className="h-12 rounded-xl bg-transparent text-white border-white/30 hover:bg-white/10 hover:text-white" disabled={!moved} onClick={() => { setPts(start); setMoved(false); }} data-testid="corner-reset-btn">
          <Undo2 className="w-4 h-4 mr-1.5" /> Reset
        </Button>
        <Button className="h-12 rounded-xl bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-900 disabled:text-white/50" disabled={blocked}
          onClick={() => onDone(pts.map((p) => ({ x: p.x * W, y: p.y * H })))} data-testid="corner-apply-btn">
          <Check className="w-4 h-4 mr-1.5" /> Confirm
        </Button>
      </div>
    </div>
  );
};
